/**
 * O que faz: Primeira etapa do assistente de nova campanha — escolher um modelo de follow-up
 *            em 3 níveis: sequências completas (recomendadas), modelos por momento da conversa
 *            (presets globais do banco) e "começar do zero" (botão no rodapé do assistente).
 * Onde usa: FollowupWizard, etapa "Modelo" (só ao criar campanha nova).
 * Conexões: useFollowupPresets, Tabs compartilhado e helpers de src/features/followup/apresentacao.ts.
 * Tabelas: follow_up_campaigns (SELECT) e follow_up_templates (SELECT) pelo hook useFollowupPresets.
 * Autor: IA
 * Data: 23/04/2026 (reorganizado em níveis e levado para dentro do assistente em 07/10/2026).
 */

import { useMemo, useState } from 'react';
import { ArrowRight, CalendarClock, CircleDot, Image as ImageIcon, Layers3, Route, Sparkles, Zap, type LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFollowupPresets } from '@/hooks/useFollowupPresets';
import { montarLinhaDoTempoFollowup, resumirGatilhoFollowup } from '@/features/followup/apresentacao';
import type { FollowupCampaign } from '@/types/followup';

interface PresetPickerProps {
  onSelect: (preset: FollowupCampaign) => void;
}

// Monta presets locais com o mesmo formato do Supabase para reaproveitar o wizard existente.
// Motivo: essas sequências são orientação de produto e funcionam mesmo antes de uma nova migration em produção.
const createStrategyPreset = (
  slug: string,
  nome: string,
  descricao: string,
  janelaMinutos: number,
  pausaAntiSpamDias: number,
  apenasLeadsAnuncio: boolean,
  templates: Array<{ titulo: string; intervaloMinutos: number; objetivo: string }>,
  promptBase: string,
): FollowupCampaign => ({
  id: `strategy-${slug}`,
  clinica_id: null,
  nome,
  descricao,
  tipo: 'automatico',
  ativo: false,
  dias_inatividade: Math.max(1, Math.ceil(janelaMinutos / 1440)),
  janela_inatividade_minutos: janelaMinutos,
  janela_maxima_inatividade_minutos: janelaMinutos <= 1440 ? 1440 : Math.max(janelaMinutos + 1440, janelaMinutos * 2),
  horario_envio: '09:00',
  horario_limite_envio: '20:00',
  modo_envio: 'apos_mensagem',
  apenas_dias_uteis: false,
  fuso_horario: 'America/Sao_Paulo',
  tipo_gatilho: 'inatividade_geral',
  padrao_gatilho_texto: null,
  padrao_match_type: 'contains',
  usa_ia: true,
  ia_prompt_base: promptBase,
  auto_pausar_ao_responder: true,
  apenas_leads_anuncio: apenasLeadsAnuncio,
  // Mantém o preset compatível com o formato da campanha mesmo sem filtro por nome de anúncio.
  ad_name_filtro: null,
  apenas_link_personalizado: false,
  cooldown_dias_relancamento: pausaAntiSpamDias,
  limite_diario: 50,
  is_preset: true,
  preset_slug: slug,
  preset_categoria: 'Sequência estratégica',
  frases_sem_followup: null,
  usar_classificacao_inteligente: true,
  follow_up_templates: templates.map((template, index) => ({
    id: `strategy-${slug}-template-${index + 1}`,
    campaign_id: `strategy-${slug}`,
    sequencia: index + 1,
    titulo: template.titulo,
    conteudo: template.objetivo,
    tipo_mensagem: 'text',
    intervalo_dias: index === 0 ? 0 : Math.max(1, Math.ceil(template.intervaloMinutos / 1440)),
    intervalo_minutos: index === 0 ? 0 : template.intervaloMinutos,
    ativo: true,
    gerado_por_ia: true,
    // Completa o formato legado esperado pela tela para um preset local sem mídia.
    ia_modelo: null,
    imagem_object_key: null,
    imagem_bucket: null,
    imagem_mimetype: null,
    imagem_nome_arquivo: null,
  })),
});

const STRATEGIC_SEQUENCE_PRESETS: FollowupCampaign[] = [
  createStrategyPreset(
    'anuncio-mesmo-dia',
    'Sequência curta para lead de anúncio',
    'Recupera leads quentes no mesmo dia com 3 tentativas leves. Ideal quando o lead clicou em anúncio e parou após a resposta.',
    15,
    3,
    true,
    [
      { titulo: '15 min - facilitar resposta', intervaloMinutos: 0, objetivo: 'Mensagem curta para facilitar uma resposta simples, sem cobrar.' },
      { titulo: '3 h - retomar interesse', intervaloMinutos: 165, objetivo: 'Retomar o assunto real do anúncio ou da conversa e oferecer ajuda no próximo passo.' },
      { titulo: 'Final do dia - encerrar leve', intervaloMinutos: 300, objetivo: 'Fechar o ciclo do mesmo dia com abertura para o lead responder quando puder.' },
    ],
    [
      'Cadência curta para lead quente vindo de anúncio.',
      'A mensagem deve ser curta, contextual e sem pressão.',
      'Use o histórico real para entender o assunto que gerou interesse.',
      'Não invente preço, prazo, oferta, disponibilidade ou benefício.',
      'Na última etapa do dia, deixe a porta aberta sem parecer cobrança.',
    ].join('\n'),
  ),
  createStrategyPreset(
    'acompanhamento-proximos-dias',
    'Acompanhamento nos próximos dias',
    'Fluxo de 1 a 4 dias para leads que ainda lembram da conversa, mas adiaram a decisão.',
    1440,
    7,
    false,
    [
      { titulo: 'D+1 - retomar contexto', intervaloMinutos: 0, objetivo: 'Retomar o assunto conversado ontem e perguntar se ainda faz sentido continuar.' },
      { titulo: 'D+2 - remover dúvida', intervaloMinutos: 1440, objetivo: 'Oferecer ajuda para tirar dúvida ou explicar o próximo passo de forma simples.' },
      { titulo: 'D+3 - oferecer alternativa', intervaloMinutos: 1440, objetivo: 'Sugerir uma alternativa de caminho, horário, opção ou formato de atendimento.' },
      { titulo: 'D+4 - pausar com respeito', intervaloMinutos: 1440, objetivo: 'Encerrar a sequência de forma educada, deixando claro que o lead pode retomar quando quiser.' },
    ],
    [
      'Cadência média para acompanhamento nos próximos dias.',
      'Não trate o lead como perdido; trate como alguém que pode ter adiado a decisão.',
      'Cada etapa deve reduzir fricção: contexto, dúvida, alternativa e encerramento respeitoso.',
      'Não invente informações que não estejam no histórico.',
      'Não use tom insistente.',
    ].join('\n'),
  ),
  createStrategyPreset(
    'reativacao-progressiva',
    'Reativação progressiva 7, 15 e 30 dias',
    'Reabre conversas frias sem depender demais do anúncio original. Boa para clínicas, viagens, serviços e propostas antigas.',
    10080,
    30,
    false,
    [
      { titulo: '7 dias - ainda faz sentido?', intervaloMinutos: 0, objetivo: 'Retomar o interesse original de forma leve, porque o lead ainda pode lembrar do contato.' },
      { titulo: '15 dias - ajuda consultiva', intervaloMinutos: 11520, objetivo: 'Oferecer ajuda para avaliar opções, tirar dúvidas ou adaptar o próximo passo.' },
      { titulo: '30 dias - reabrir conversa', intervaloMinutos: 21600, objetivo: 'Reabrir sem assumir que o lead ainda quer exatamente a mesma coisa.' },
    ],
    [
      'Cadência longa de reativação.',
      'Com 7 dias, pode retomar o contexto original se ele estiver claro.',
      'Com 15 dias, seja mais consultivo e menos direto.',
      'Com 30 dias, reabra a conversa sem assumir que o anúncio ou proposta original ainda está fresco.',
      'Não invente novidade, desconto, urgência ou informação clínica.',
    ].join('\n'),
  ),
  createStrategyPreset(
    'funil-completo-seguro',
    'Funil completo: curto + médio + reativação',
    'Sequência ampla para operar do lead quente até reativação de 30 dias, com pausas progressivas.',
    30,
    30,
    false,
    [
      { titulo: '30 min - recuperar atenção', intervaloMinutos: 0, objetivo: 'Facilitar uma resposta rápida enquanto o assunto ainda está quente.' },
      { titulo: '3 h - próximo passo', intervaloMinutos: 150, objetivo: 'Retomar o assunto pendente e convidar para um próximo passo simples.' },
      { titulo: 'D+1 - contexto do dia seguinte', intervaloMinutos: 1260, objetivo: 'Retomar a conversa no dia seguinte sem parecer cobrança.' },
      { titulo: 'D+3 - alternativa', intervaloMinutos: 2880, objetivo: 'Oferecer alternativa, tirar dúvida ou simplificar a decisão.' },
      { titulo: 'D+7 - reativação curta', intervaloMinutos: 5760, objetivo: 'Perguntar se ainda faz sentido conversar sobre o assunto original.' },
      { titulo: 'D+30 - reabertura ampla', intervaloMinutos: 33120, objetivo: 'Reabrir a conversa sem depender do contexto original, perguntando se ainda pode ajudar.' },
    ],
    [
      'Cadência completa e segura para leads que podem esfriar ao longo do tempo.',
      'Use mensagens curtas no mesmo dia, consultivas nos próximos dias e amplas depois de 30 dias.',
      'A cada etapa, adapte ao histórico real e ao momento da conversa.',
      'Nunca pressione, nunca invente informação e nunca prometa resultado.',
      'Se houver pouco contexto, faça uma pergunta simples para retomar.',
    ].join('\n'),
  ),
];


interface CategoriaModelo {
  rotulo: string;
  icone: LucideIcon;
  descricao: string;
}

// O banco usa chaves curtas (curto, medio...) e, em presets antigos, nomes por extenso.
// Os dois formatos caem no mesmo grupo para a aba não aparecer duplicada.
const CATEGORIAS_MODELO: Record<string, CategoriaModelo> = {
  curto: { rotulo: 'Mesmo dia', icone: Zap, descricao: 'Para conversas que esfriaram há poucos minutos ou horas.' },
  medio: { rotulo: 'Próximos dias', icone: Layers3, descricao: 'Para quem ainda lembra da conversa, mas adiou a decisão (1 a 4 dias).' },
  especifico: { rotulo: 'Por situação', icone: CircleDot, descricao: 'Para casos com contexto claro, como orçamento enviado ou agendamento pendente.' },
  longo: { rotulo: 'Reativação', icone: CalendarClock, descricao: 'Para leads frios, com mensagens espaçadas e sem pressão.' },
};
const ALIAS_CATEGORIA: Record<string, string> = {
  'Curto prazo': 'curto',
  'Médio prazo': 'medio',
  'Específico': 'especifico',
  'Longo prazo': 'longo',
};
// Ordem temporal fixa: do lead quente ao lead frio, independente da ordem que o banco devolve.
const ORDEM_CATEGORIAS = ['curto', 'medio', 'especifico', 'longo'];

const normalizarCategoria = (categoria: string | null): string =>
  categoria ? (ALIAS_CATEGORIA[categoria] ?? categoria) : 'outros';

const dadosCategoria = (chave: string): CategoriaModelo =>
  CATEGORIAS_MODELO[chave] ?? { rotulo: chave === 'outros' ? 'Outros' : chave, icone: Sparkles, descricao: 'Modelos prontos de reativação.' };

interface CartaoModeloProps {
  modelo: FollowupCampaign;
  onSelect: (modelo: FollowupCampaign) => void;
  destaque?: boolean;
}

/** Cartão inteiro clicável: um clique escolhe o modelo e o assistente avança para "Dados". */
const CartaoModelo = ({ modelo, onSelect, destaque = false }: CartaoModeloProps) => {
  const passos = montarLinhaDoTempoFollowup(modelo.janela_inatividade_minutos, modelo.follow_up_templates ?? []);
  const quantidade = passos.length;

  return (
    <button
      type="button"
      onClick={() => onSelect(modelo)}
      className="group flex h-full w-full flex-col gap-3 rounded-2xl border border-border bg-card p-4 text-left shadow-[var(--lg-sombra-1)] transition-[box-shadow,border-color] duration-200 hover:border-input hover:shadow-[var(--lg-sombra-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 items-start gap-2">
          {/* Ícone pequeno e inline: marca as sequências completas sem roubar largura do título. */}
          {destaque && <Route className="mt-0.5 h-4 w-4 shrink-0 text-accent-foreground" aria-hidden="true" />}
          <span className="font-display text-[15px] font-semibold leading-snug text-foreground">{modelo.nome}</span>
        </span>
        <span className="lg-selo lg-selo--neutro shrink-0">
          {quantidade} {quantidade === 1 ? 'mensagem' : 'mensagens'}
        </span>
      </span>

      {modelo.descricao && (
        <span className="line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">{modelo.descricao}</span>
      )}

      {/* Linha do tempo resumida: mostra o ritmo da sequência sem precisar abrir o modelo. */}
      {quantidade > 0 && (
        <span className="flex flex-wrap items-center gap-1" aria-label={`Ritmo: ${passos.map((passo) => passo.momento).join(', ')}`}>
          {passos.map((passo, indice) => (
            <span key={passo.sequencia} className="flex items-center gap-1" aria-hidden="true">
              {indice > 0 && <span className="h-px w-2 bg-border" />}
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-foreground">
                {passo.momento}
                {passo.temImagem && <ImageIcon className="h-3 w-3" />}
              </span>
            </span>
          ))}
        </span>
      )}

      <span className="text-xs leading-relaxed text-muted-foreground">
        {resumirGatilhoFollowup(modelo)}
        {modelo.apenas_leads_anuncio ? ' · só leads de anúncio' : ''}
      </span>

      <span className="mt-auto inline-flex items-center gap-1 text-[13px] font-semibold text-accent-foreground">
        Usar este modelo
        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
      </span>
    </button>
  );
};

export const PresetPicker = ({ onSelect }: PresetPickerProps) => {
  const { data: presets = [], isLoading } = useFollowupPresets();

  // Agrupa os presets do banco por categoria normalizada e na ordem temporal.
  const grupos = useMemo(() => {
    const porCategoria = new Map<string, FollowupCampaign[]>();
    for (const preset of presets) {
      const chave = normalizarCategoria(preset.preset_categoria);
      porCategoria.set(chave, [...(porCategoria.get(chave) ?? []), preset]);
    }
    return [...porCategoria.entries()].sort(([primeira], [segunda]) => {
      const indicePrimeira = ORDEM_CATEGORIAS.indexOf(primeira);
      const indiceSegunda = ORDEM_CATEGORIAS.indexOf(segunda);
      return (indicePrimeira === -1 ? ORDEM_CATEGORIAS.length : indicePrimeira)
        - (indiceSegunda === -1 ? ORDEM_CATEGORIAS.length : indiceSegunda);
    });
  }, [presets]);

  const [abaEscolhida, setAbaEscolhida] = useState<string | null>(null);
  const abaAtual = abaEscolhida ?? grupos[0]?.[0] ?? '';

  return (
    <div className="space-y-8">
      {/* Nível 1 — recomendado: cadências completas que já funcionam do lead quente ao frio. */}
      <section aria-labelledby="modelos-recomendados" className="space-y-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 id="modelos-recomendados" className="text-base text-foreground">Sequências completas</h4>
            <span className="lg-selo bg-accent text-accent-foreground">Recomendado</span>
          </div>
          <p className="text-[13px] text-muted-foreground">
            Cadências prontas que combinam o mesmo dia, os próximos dias e a reativação. Você revisa tudo antes de salvar.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {STRATEGIC_SEQUENCE_PRESETS.map((modelo) => (
            <CartaoModelo key={modelo.id} modelo={modelo} onSelect={onSelect} destaque />
          ))}
        </div>
      </section>

      {/* Nível 2 — modelos curtos por momento da conversa (presets globais do banco). */}
      <section aria-labelledby="modelos-por-momento" className="space-y-3 border-t border-border pt-6">
        <div className="space-y-1">
          <h4 id="modelos-por-momento" className="text-base text-foreground">Modelos por momento da conversa</h4>
          <p className="text-[13px] text-muted-foreground">Campanhas menores, para um momento específico do atendimento.</p>
        </div>

        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2" aria-busy="true" aria-label="Carregando modelos">
            {[0, 1].map((indice) => <Skeleton key={indice} className="h-40 rounded-2xl bg-secondary" />)}
          </div>
        ) : grupos.length === 0 ? (
          <p className="rounded-2xl bg-muted px-4 py-3 text-[13px] text-muted-foreground">
            Nenhum modelo extra disponível agora. Use uma sequência completa ou comece do zero.
          </p>
        ) : (
          <Tabs value={abaAtual} onValueChange={setAbaEscolhida}>
            {/* Trilho com rolagem própria no celular, sem empurrar a página para o lado. */}
            <div className="-mx-1 overflow-x-auto px-1 pb-1">
              <TabsList className="h-auto bg-secondary">
                {grupos.map(([chave, itens]) => {
                  const { rotulo, icone: Icone } = dadosCategoria(chave);
                  return (
                    <TabsTrigger key={chave} value={chave} className="gap-1.5 px-3 py-1.5 text-[13px]">
                      <Icone className="h-3.5 w-3.5" aria-hidden="true" />
                      {rotulo}
                      <span className="tabular-nums text-muted-foreground">{itens.length}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
            {grupos.map(([chave, itens]) => (
              <TabsContent key={chave} value={chave} className="mt-3 space-y-3">
                <p className="text-[13px] text-muted-foreground">{dadosCategoria(chave).descricao}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {itens.map((modelo) => (
                    <CartaoModelo key={modelo.id} modelo={modelo} onSelect={onSelect} />
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </section>
    </div>
  );
};
